import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Check,
  AlertCircle,
  Loader,
  ArrowRight,
  Navigation,
  CheckCircle,
  Clock,
  MapPin,
  XCircle,
} from "lucide-react";
import apiService from "../services/api";

const EditAppointmentModal = ({
  editingAppointment,
  teamMembers,
  userContext,
  isEngineeringTeam,
  fetchAppointments,
  onClose,
}) => {
  // Helper to format date for datetime-local input (preserves DB time as-is)
  const formatDatetimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Default cancelledAt to today in datetime-local format
  const todayDatetimeLocal = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const [formData, setFormData] = useState({
    title: editingAppointment?.title || "",
    description: editingAppointment?.description || "",
    startTime: editingAppointment?.startTime
      ? editingAppointment.startTime.slice(0, 16)
      : "",
    endTime: editingAppointment?.endTime
      ? editingAppointment.endTime.slice(0, 16)
      : "",
    type: editingAppointment?.type || "meeting",
    salesType: editingAppointment?.salesType || "",
    priority: editingAppointment?.priority || "medium",
    visibility: editingAppointment?.visibility || "team",
    location: editingAppointment?.location || "",
    meetingUrl: editingAppointment?.meetingUrl || "",
    assignedTo: editingAppointment?.assignedTo || "",
    installationBookedAs: editingAppointment?.installationBookedAs || "",
    installationOutcome: editingAppointment?.installationOutcome || "",
    installationOutcomeNotes:
      editingAppointment?.installationOutcomeNotes || "",
    saleOriginated: editingAppointment?.saleOriginated || "",
    status: editingAppointment?.status || "scheduled",
    cancelledAt: editingAppointment?.cancelledAt
      ? editingAppointment.cancelledAt.slice(0, 16)
      : todayDatetimeLocal(),
    cancellationReason: editingAppointment?.cancellationReason || "",
  });

  const isCancelling = formData.status === "cancelled";
  const wasAlreadyCancelled = editingAppointment?.status === "cancelled";

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [viabilityCheck, setViabilityCheck] = useState(null);
  const [checkingViability, setCheckingViability] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationInput, setLocationInput] = useState(
    editingAppointment?.location || "",
  );
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [fetchingPredictions, setFetchingPredictions] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(
    !!editingAppointment?.locationLat,
  );
  const locationDropdownRef = React.useRef(null);

  // Linked opportunity info — shown when cancelling
  const linkedOpportunity = editingAppointment?.opportunity || null;

  const isManager = userContext?.roles?.includes("manager");
  const isInManagementTeam = userContext?.teamTypes?.includes("management");
  const isEngineeringAppointment = isEngineeringTeam;

  const virtualKeywords = [
    "zoom",
    "teams",
    "meet",
    "skype",
    "phone",
    "call",
    "virtual",
    "online",
    "remote",
  ];
  const isVirtualLocation = (input) =>
    virtualKeywords.some((kw) => input.toLowerCase().includes(kw));

  const predictionsCache = React.useRef({});
  const lastFetchedQuery = React.useRef("");
  const MIN_CHARS = 5;
  const DEBOUNCE_MS = 600;

  const fetchPredictions = useCallback(async (input) => {
    const normalised = input.trim().toLowerCase();
    if (!normalised || normalised.length < MIN_CHARS) {
      setPlacePredictions([]);
      setShowPlaceDropdown(false);
      return;
    }
    if (isVirtualLocation(normalised)) {
      setPlacePredictions([]);
      setShowPlaceDropdown(false);
      return;
    }
    if (normalised === lastFetchedQuery.current) return;
    if (predictionsCache.current[normalised]) {
      setPlacePredictions(predictionsCache.current[normalised]);
      setShowPlaceDropdown(true);
      return;
    }
    lastFetchedQuery.current = normalised;
    setFetchingPredictions(true);
    try {
      const response = await apiService.getPlacePredictions(input.trim());
      if (response.success && response.predictions?.length > 0) {
        const limited = response.predictions.slice(0, 5);
        predictionsCache.current[normalised] = limited;
        setPlacePredictions(limited);
        setShowPlaceDropdown(true);
      } else {
        predictionsCache.current[normalised] = [];
        setPlacePredictions([]);
        setShowPlaceDropdown(false);
      }
    } catch (err) {
      console.error("Autocomplete error:", err);
      setPlacePredictions([]);
    } finally {
      setFetchingPredictions(false);
    }
  }, []);

  // Debounce predictions
  useEffect(() => {
    if (locationConfirmed) return;
    const timer = setTimeout(
      () => fetchPredictions(locationInput),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [locationInput, locationConfirmed, fetchPredictions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(e.target)
      ) {
        setShowPlaceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocationInputChange = (e) => {
    const val = e.target.value;
    setLocationInput(val);
    setLocationConfirmed(false);
    setFormData((prev) => ({
      ...prev,
      location: val,
      locationLat: null,
      locationLng: null,
      locationPlaceId: null,
    }));
  };

  const handleSelectPlace = async (prediction) => {
    setShowPlaceDropdown(false);
    setLocationInput(prediction.description);
    setLocationConfirmed(true);
    if (prediction.lat && prediction.lng) {
      setFormData((prev) => ({
        ...prev,
        location: prediction.description,
        locationLat: prediction.lat,
        locationLng: prediction.lng,
        locationPlaceId: prediction.placeId || null,
      }));
    } else {
      try {
        const response = await apiService.geocodeAddress(
          prediction.description,
        );
        if (response.success && response.location) {
          setFormData((prev) => ({
            ...prev,
            location: prediction.description,
            locationLat: response.location.lat,
            locationLng: response.location.lng,
            locationPlaceId: prediction.placeId || response.location.placeId,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            location: prediction.description,
            locationLat: null,
            locationLng: null,
            locationPlaceId: prediction.placeId || null,
          }));
        }
      } catch (err) {
        console.error("Geocode on select error:", err);
        setFormData((prev) => ({
          ...prev,
          location: prediction.description,
          locationPlaceId: prediction.placeId || null,
        }));
      }
    }
  };

  useEffect(() => {
    // Skip viability check when cancelling
    if (isCancelling) {
      setViabilityCheck(null);
      return;
    }

    const checkViability = async () => {
      const timeChanged =
        formData.startTime !==
          new Date(editingAppointment.startTime).toISOString().slice(0, 16) ||
        formData.endTime !==
          new Date(editingAppointment.endTime).toISOString().slice(0, 16);
      const assigneeChanged =
        formData.assignedTo !== editingAppointment.assignedTo;
      const locationChanged = formData.location !== editingAppointment.location;

      if (!timeChanged && !assigneeChanged && !locationChanged) {
        setViabilityCheck(null);
        return;
      }

      if (!formData.startTime || !formData.endTime || !formData.assignedTo) {
        setViabilityCheck(null);
        return;
      }

      setCheckingViability(true);
      setError("");

      try {
        const response = await apiService.checkAppointmentViability({
          ...formData,
          excludeAppointmentId: editingAppointment.id,
        });

        setViabilityCheck(response);

        if (!response.viable) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error checking viability:", error);
        setViabilityCheck(null);
      } finally {
        setCheckingViability(false);
      }
    };

    const timeoutId = setTimeout(() => {
      checkViability();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    formData.startTime,
    formData.endTime,
    formData.assignedTo,
    formData.location,
    isCancelling,
  ]);

  const applySuggestion = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      startTime: new Date(suggestion.startTime).toISOString().slice(0, 16),
      endTime: new Date(suggestion.endTime).toISOString().slice(0, 16),
    }));
    setShowSuggestions(false);
    setViabilityCheck(null);
  };

  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const buildPayload = (extra = {}) => ({
    ...formData,
    installationOutcome: formData.installationOutcome || null,
    installationBookedAs: formData.installationBookedAs || null,
    installationOutcomeNotes: formData.installationOutcomeNotes || null,
    // Only send cancellation fields when actually cancelling
    cancelledAt: isCancelling ? formData.cancelledAt : undefined,
    cancellationReason: isCancelling
      ? formData.cancellationReason || undefined
      : undefined,
    ...extra,
  });

  const forceUpdate = async () => {
    if (!isManager) {
      setError("Only managers can override scheduling conflicts");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await apiService.updateAppointment(
        editingAppointment.id,
        buildPayload({ force: true }),
      );

      if (response.success) {
        onClose();
        await fetchAppointments();
      } else {
        setError(response.message || "Failed to update appointment");
      }
    } catch (error) {
      console.error("Error updating appointment:", error);
      setError(error.response?.data?.message || "Failed to update appointment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.assignedTo) {
      setError("Please select a team member to assign this appointment to");
      return;
    }

    if (isCancelling && !formData.cancelledAt) {
      setError("Please select the cancellation date");
      return;
    }

    if (viabilityCheck && !viabilityCheck.viable && !isManager) {
      setError(
        "Please resolve scheduling conflicts or select a suggested time",
      );
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await apiService.updateAppointment(
        editingAppointment.id,
        buildPayload(),
      );

      if (response.success) {
        onClose();
        await fetchAppointments();
      } else {
        if (response.conflicts) {
          setViabilityCheck({
            viable: false,
            message: response.message,
            conflicts: response.conflicts,
            suggestions: response.suggestions,
          });
          setShowSuggestions(true);
          setError(response.message);
        } else {
          setError(response.message || "Failed to update appointment");
        }
      }
    } catch (error) {
      console.error("Error updating appointment:", error);

      if (error.response?.status === 409) {
        const errorData = error.response.data;
        setViabilityCheck({
          viable: false,
          message: errorData.message,
          conflicts: errorData.conflicts,
          suggestions: errorData.suggestions,
        });
        setShowSuggestions(true);
        setError(errorData.message);
      } else {
        setError(
          error.response?.data?.message || "Failed to update appointment",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#13161B] rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-4 sm:p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-[#13161B] z-10">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            Edit Appointment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 sm:p-6 space-y-4 sm:space-y-5"
        >
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle
                size={20}
                className="text-red-500 mt-0.5 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-red-500 font-semibold">Error</div>
                <div className="text-red-400 text-sm break-words">{error}</div>
              </div>
              <button
                type="button"
                onClick={() => setError("")}
                className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="Enter appointment title"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Assign To *
            </label>
            <select
              required
              value={formData.assignedTo}
              onChange={(e) =>
                setFormData({ ...formData, assignedTo: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select team member</option>
              {teamMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.user.name} ({member.role})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="no_show">No Show</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* ── Cancellation fields ── shown only when status = cancelled */}
          {isCancelling && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <XCircle size={16} className="text-red-400" />
                <span className="text-red-300 font-semibold text-sm">
                  Cancellation Details
                </span>
              </div>

              {/* Warn that the linked opportunity will also be cancelled */}
              {editingAppointment?.opportunityId && !wasAlreadyCancelled && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3 flex items-start gap-2">
                  <AlertCircle
                    size={15}
                    className="text-yellow-400 flex-shrink-0 mt-0.5"
                  />
                  <p className="text-yellow-300 text-xs leading-relaxed">
                    The linked opportunity will also be marked as{" "}
                    <strong>Cancelled</strong> using the same date and reason.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Cancelled At *
                </label>
                <input
                  type="datetime-local"
                  required={isCancelling}
                  value={formData.cancelledAt}
                  onChange={(e) =>
                    setFormData({ ...formData, cancelledAt: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-red-500/40 rounded-lg text-white focus:border-red-400 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Cancellation Reason
                </label>
                <textarea
                  value={formData.cancellationReason}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cancellationReason: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-red-500/40 rounded-lg text-white focus:border-red-400 focus:outline-none transition-colors"
                  rows={3}
                  placeholder="Reason for cancellation..."
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Visibility
            </label>
            <select
              value={formData.visibility}
              onChange={(e) =>
                setFormData({ ...formData, visibility: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="team">Team - Visible to team members</option>
              <option value="public">Public - Visible to entire company</option>
              <option value="private">Private - Only you and assignee</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              rows={3}
              placeholder="Add description..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Start Time *
              </label>
              <input
                type="datetime-local"
                required
                value={formData.startTime}
                onChange={(e) =>
                  setFormData({ ...formData, startTime: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                End Time *
              </label>
              <input
                type="datetime-local"
                required
                value={formData.endTime}
                onChange={(e) =>
                  setFormData({ ...formData, endTime: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="meeting">Online Meeting</option>
                <option value="call">Call</option>
                <option value="site_visit">Site Visit</option>
                <option value="consultation">Consultation</option>
                <option value="demo">Demo</option>
                <option value="follow_up">Follow Up</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Appointment Type
              </label>
              <select
                value={formData.salesType}
                onChange={(e) =>
                  setFormData({ ...formData, salesType: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="">Not Set</option>
                <option value="base">Base</option>
                <option value="new_business">New Business</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Sale Originated
            </label>
            <select
              value={formData.saleOriginated}
              onChange={(e) =>
                setFormData({ ...formData, saleOriginated: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Not Set</option>
              <option value="cold_call">Cold Call</option>
              <option value="reschedule">Reschedule</option>
              <option value="email">Email</option>
              <option value="base">Base</option>
              <option value="website_enquiry">Website Enquiry</option>
              <option value="incoming_call">Incoming Call</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
              Location
            </label>
            <div className="relative" ref={locationDropdownRef}>
              <input
                type="text"
                value={locationInput}
                onChange={handleLocationInputChange}
                onFocus={() => {
                  if (placePredictions.length > 0 && !locationConfirmed) {
                    setShowPlaceDropdown(true);
                  }
                }}
                className={`w-full px-4 py-3 bg-[#0C0E12] border rounded-lg text-white focus:outline-none transition-colors pr-10 ${
                  locationConfirmed && formData.locationLat
                    ? "border-green-500/70 focus:border-green-500"
                    : "border-gray-700 focus:border-blue-500"
                }`}
                placeholder="Search for an address..."
                autoComplete="off"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {fetchingPredictions ? (
                  <Loader className="animate-spin text-blue-400" size={16} />
                ) : locationConfirmed && formData.locationLat ? (
                  <CheckCircle size={16} className="text-green-400" />
                ) : locationInput && !locationConfirmed ? (
                  <MapPin size={16} className="text-gray-500" />
                ) : null}
              </div>

              {/* Autocomplete Dropdown */}
              {showPlaceDropdown && placePredictions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-[#1a1d24] border border-gray-700/70 rounded-xl shadow-2xl overflow-hidden">
                  {placePredictions.map((prediction, index) => (
                    <button
                      key={prediction.placeId || index}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectPlace(prediction);
                      }}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-700/50 transition-colors duration-150 text-left border-b border-gray-800/50 last:border-0"
                    >
                      <MapPin
                        size={14}
                        className="text-blue-400 flex-shrink-0 mt-0.5"
                      />
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">
                          {prediction.mainText || prediction.description}
                        </div>
                        {prediction.secondaryText && (
                          <div className="text-gray-400 text-xs truncate mt-0.5">
                            {prediction.secondaryText}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {locationInput &&
              locationInput.trim().length < MIN_CHARS &&
              !isVirtualLocation(locationInput) && (
                <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                  <MapPin size={11} />
                  Keep typing to search (
                  {MIN_CHARS - locationInput.trim().length} more character
                  {MIN_CHARS - locationInput.trim().length !== 1 ? "s" : ""}
                  ...)
                </p>
              )}
            {locationInput &&
              locationInput.trim().length >= MIN_CHARS &&
              !locationConfirmed &&
              !fetchingPredictions &&
              !isVirtualLocation(locationInput) && (
                <p className="mt-1.5 text-xs text-yellow-400/80 flex items-center gap-1">
                  <AlertCircle size={11} />
                  Select an address from the dropdown to confirm location
                </p>
              )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Meeting URL
            </label>
            <input
              type="url"
              value={formData.meetingUrl}
              onChange={(e) =>
                setFormData({ ...formData, meetingUrl: e.target.value })
              }
              className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="https://zoom.us/j/..."
            />
          </div>

          {isEngineeringAppointment && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Installation Booked As
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <select
                  name="installationBookedAs"
                  value={formData.installationBookedAs}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      installationBookedAs: e.target.value,
                    })
                  }
                  disabled={!isInManagementTeam}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors appearance-none ${
                    !isInManagementTeam
                      ? "bg-gray-900/30 border-gray-700/30 text-gray-400 cursor-not-allowed opacity-60"
                      : "bg-[#0C0E12] border-gray-700 text-white focus:border-blue-500 focus:outline-none cursor-pointer"
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23${!isInManagementTeam ? "6B7280" : "9CA3AF"}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                  }}
                >
                  <option value="">Select type</option>
                  <option value="new_install">New Install</option>
                  <option value="base_install">Base Install</option>
                  <option value="revisit_for_support">
                    Revisit for Support
                  </option>
                  <option value="broadband_connection">
                    Broadband Connection
                  </option>
                  <option value="revisit_install_problem">
                    Revisit Install Problem
                  </option>
                  <option value="webex_migration">Webex Migration</option>
                </select>
                {!isInManagementTeam && (
                  <p className="text-xs text-gray-500 mt-2">
                    Only management team members can edit this field
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Installation Outcome
                </label>
                <select
                  name="installationOutcome"
                  value={formData.installationOutcome}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      installationOutcome: e.target.value,
                    })
                  }
                  disabled={!isInManagementTeam}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors appearance-none ${
                    !isInManagementTeam
                      ? "bg-gray-900/30 border-gray-700/30 text-gray-400 cursor-not-allowed opacity-60"
                      : "bg-[#0C0E12] border-gray-700 text-white focus:border-blue-500 focus:outline-none cursor-pointer"
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23${!isInManagementTeam ? "6B7280" : "9CA3AF"}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                  }}
                >
                  <option value="">Not set</option>
                  <option value="new_install">New Install</option>
                  <option value="base_install">Base Install</option>
                  <option value="revisit_for_support">
                    Revisit for Support
                  </option>
                  <option value="broadband_connection">
                    Broadband Connection
                  </option>
                  <option value="revisit_install_problem">
                    Revisit Install Problem
                  </option>
                  <option value="webex_migration">Webex Migration</option>
                </select>
                {!isInManagementTeam && (
                  <p className="text-xs text-gray-500 mt-2">
                    Only management team members can edit this field
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Installation Outcome Notes
                </label>
                <textarea
                  value={formData.installationOutcomeNotes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      installationOutcomeNotes: e.target.value,
                    })
                  }
                  disabled={!isInManagementTeam}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors appearance-none ${
                    !isInManagementTeam
                      ? "bg-gray-900/30 border-gray-700/30 text-gray-400 cursor-not-allowed opacity-60"
                      : "bg-[#0C0E12] border-gray-700 text-white focus:border-blue-500 focus:outline-none cursor-pointer"
                  }`}
                  rows={3}
                  placeholder="Add installation outcome notes..."
                />
                {!isInManagementTeam && (
                  <p className="text-xs text-gray-500 mt-2">
                    Only management team members can edit this field
                  </p>
                )}
              </div>
            </>
          )}

          {checkingViability && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center gap-3">
              <Loader size={20} className="animate-spin text-blue-400" />
              <span className="text-blue-300 text-sm font-medium">
                Checking availability...
              </span>
            </div>
          )}

          {viabilityCheck && !checkingViability && (
            <div
              className={`border rounded-xl p-5 ${
                viabilityCheck.viable
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-yellow-500/10 border-yellow-500/30"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: viabilityCheck.viable
                      ? "rgba(34, 197, 94, 0.2)"
                      : "rgba(234, 179, 8, 0.2)",
                  }}
                >
                  {viabilityCheck.viable ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : (
                    <AlertCircle size={16} className="text-yellow-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div
                    className={`font-semibold mb-3 ${
                      viabilityCheck.viable
                        ? "text-green-300"
                        : "text-yellow-300"
                    }`}
                  >
                    {viabilityCheck.message}
                  </div>

                  {viabilityCheck.conflicts &&
                    viabilityCheck.conflicts.length > 0 && (
                      <div className="space-y-3">
                        {viabilityCheck.conflicts.map((conflict, index) => (
                          <div
                            key={index}
                            className="bg-black/20 rounded-lg p-4 border border-white/5"
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-6 h-6 rounded-md bg-yellow-500/20 flex items-center justify-center">
                                <Navigation
                                  size={12}
                                  className="text-yellow-400"
                                />
                              </div>
                              <span className="font-medium text-white text-sm">
                                {conflict.type ===
                                "insufficient_travel_time_before"
                                  ? "Not enough time from previous appointment"
                                  : "Not enough time to next appointment"}
                              </span>
                            </div>
                            {conflict.travelInfo && (
                              <div className="text-xs text-gray-400 space-y-2 pl-8">
                                <div className="flex items-center justify-between py-1">
                                  <span className="text-gray-500">
                                    Required:
                                  </span>
                                  <span className="font-medium">
                                    {conflict.requiredTravelTime} minutes (with
                                    buffer)
                                  </span>
                                </div>
                                <div className="flex items-center justify-between py-1">
                                  <span className="text-gray-500">
                                    Available:
                                  </span>
                                  <span className="font-medium">
                                    {conflict.availableTime} minutes
                                  </span>
                                </div>
                                <div className="flex items-center justify-between py-1">
                                  <span className="text-gray-500">
                                    Distance:
                                  </span>
                                  <span className="font-medium">
                                    {conflict.travelInfo.distance} •{" "}
                                    {conflict.travelInfo.estimatedTime}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {showSuggestions &&
            viabilityCheck?.suggestions &&
            viabilityCheck.suggestions.length > 0 && (
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-xl p-5 animate-slideDown">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Clock size={16} className="text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-blue-300">
                    Suggested Alternative Times
                  </h3>
                </div>
                <div className="space-y-2.5 mb-4">
                  {viabilityCheck.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className="w-full text-left bg-black/30 hover:bg-black/50 border border-gray-700/50 hover:border-blue-500/50 rounded-xl p-4 transition-all duration-200 group"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium mb-1.5">
                            {formatDateTime(suggestion.startTime)}
                          </div>
                          <div className="text-xs text-gray-400 mb-1">
                            {suggestion.reason}
                          </div>
                          {suggestion.travelInfo && (
                            <div className="text-xs text-gray-500">
                              Includes {suggestion.travelInfo.withBuffer} travel
                              time
                            </div>
                          )}
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 border border-blue-500/20 flex items-center justify-center transition-all duration-200">
                          <ArrowRight
                            size={14}
                            className="text-blue-400 group-hover:translate-x-0.5 transition-transform duration-200"
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {isManager && (
                  <div className="flex gap-3 pt-4 border-t border-gray-700/50">
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50 text-gray-300 hover:text-white rounded-xl text-sm font-medium transition-all duration-200"
                    >
                      Keep Original Time
                    </button>
                    <button
                      type="button"
                      onClick={forceUpdate}
                      disabled={submitting}
                      className="flex-1 px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg shadow-yellow-600/20 disabled:opacity-50"
                    >
                      {submitting ? "Updating..." : "Override & Update"}
                    </button>
                  </div>
                )}
              </div>
            )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || checkingViability || fetchingPredictions}
              className={`flex-1 px-4 py-3 text-white rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 hover:shadow-xl flex items-center justify-center gap-2 ${
                isCancelling ? "bg-red-600 hover:bg-red-700" : ""
              }`}
              style={
                isCancelling
                  ? undefined
                  : {
                      background:
                        "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                    }
              }
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Updating...</span>
                </>
              ) : isCancelling ? (
                <>
                  <XCircle size={18} />
                  <span>Confirm Cancellation</span>
                </>
              ) : (
                <>
                  <Check size={18} />
                  <span>Update Appointment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAppointmentModal;
